import styles from 'css/src/components/app/recorder/Prompter.module.scss';

import type { ComponentProps } from 'react';
import React from 'react';
import classNames from 'classnames';
import debounce from 'lodash/debounce';

import Button, {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_WIDTHS,
} from 'js/src/components/common/ui/Button';
import CustomValue from 'js/src/components/common/ui/Select/CustomValue';
import Select from 'js/src/components/common/ui/Select/Select';
import type { SelectOption } from 'js/src/components/common/ui/Select/types';

const PROMPTER_SCROLL_TIME_INTERVAL = 24; // ms
const PROMPTER_SELECT_SPEED_OPTIONS = [
    {
        label: 'Slow speed',
        value: 0.5,
    },
    {
        label: 'Medium speed',
        value: 1.5,
    },
    {
        label: 'Fast speed',
        value: 3,
    },
];

type PrompterProps = {
    show: boolean;
    pauseScroll?: boolean;
    onHide: () => void;
    isOverlay?: boolean;
    defaultText?: string;
};

const DEFAULT_PROMPTER_TEXT =
    'Welcome to the Teleprompter! You can edit the text here or paste your script in.';

const Prompter: React.FC<PrompterProps> = React.memo(function Prompter({
    show,
    pauseScroll = false,
    onHide,
    isOverlay = false,
    defaultText = DEFAULT_PROMPTER_TEXT,
}) {
    const prompterRef = React.useRef<HTMLHeadingElement | null>(null);
    const scrollIntervalCallback = React.useRef<number | undefined>(undefined);
    const [prompterScrollHeight, setPrompterScrollHeight] = React.useState(0);
    const [prompterSpeed, setPrompterSpeed] = React.useState(1);
    const [prompterIsFocused, setPrompterIsFocused] = React.useState(false);

    const scroll = React.useCallback(() => {
        if (!prompterIsFocused && !pauseScroll && prompterRef.current) {
            const newScrollTop = prompterScrollHeight + prompterSpeed;
            prompterRef.current.scrollTo({
                top: newScrollTop,
            });
            setPrompterScrollHeight(newScrollTop);
        }
    }, [prompterScrollHeight, prompterSpeed, prompterIsFocused, pauseScroll]);

    const handleScroll = React.useCallback(
        (e: React.UIEvent<HTMLHeadingElement>) => {
            setPrompterScrollHeight(e.currentTarget.scrollTop);
            clearInterval(scrollIntervalCallback.current);
            scrollIntervalCallback.current = window?.setInterval(
                scroll,
                PROMPTER_SCROLL_TIME_INTERVAL,
            );
        },
        [scroll],
    );

    // added debounce to alleviate jumpiness in scrolling
    const handleWheelScroll = React.useCallback(
        (e: React.UIEvent<HTMLHeadingElement>) =>
            debounce(() => {
                setPrompterScrollHeight(e.currentTarget.scrollTop);
                clearInterval(scrollIntervalCallback.current);
                scrollIntervalCallback.current = window?.setInterval(
                    scroll,
                    PROMPTER_SCROLL_TIME_INTERVAL,
                );
            }, 250),
        [scroll],
    );

    const handlePrompterFocus = React.useCallback(() => {
        clearInterval(scrollIntervalCallback.current); // stop the auto scroll when user is typing
        setPrompterIsFocused(true);
    }, []);

    const handlePrompterBlur = React.useCallback(() => {
        setPrompterIsFocused(false);
        clearInterval(scrollIntervalCallback.current);
        scrollIntervalCallback.current = window?.setInterval(scroll, PROMPTER_SCROLL_TIME_INTERVAL);
    }, [scroll]);

    const handleOnHide = React.useCallback(() => {
        typeof onHide === 'function' && onHide();
    }, [onHide]);

    const [selectedSpeedOption, setSelectedSpeedOption] = React.useState(
        PROMPTER_SELECT_SPEED_OPTIONS[1],
    );
    const handleSelectSpeed = React.useCallback((selectedSpeedValue: number) => {
        setPrompterSpeed(selectedSpeedValue);
        const speedOption = PROMPTER_SELECT_SPEED_OPTIONS.find(
            (option) => option.value === selectedSpeedValue,
        );
        if (!speedOption) throw new Error('Invalid speed option');
        setSelectedSpeedOption(speedOption);
    }, []);
    const SelectSpeedValueComponent = function SelectSpeedValue(
        selectProps: ComponentProps<typeof CustomValue<SelectOption<number>, false>>,
    ) {
        return (
            <CustomValue {...selectProps}>
                <div className="d-flex align-items-center">
                    <i translate="no" className="material-symbols-outlined me-l5-8">
                        speed
                    </i>
                    {`${selectedSpeedOption['label']}`}
                </div>
            </CustomValue>
        );
    };

    // effect to start auto scrolling by show prop
    React.useEffect(() => {
        if (prompterRef.current) {
            if (show) {
                clearInterval(scrollIntervalCallback.current); // ensure we don't callback leak
                scrollIntervalCallback.current = window?.setInterval(
                    scroll,
                    PROMPTER_SCROLL_TIME_INTERVAL,
                );
            } else {
                clearInterval(scrollIntervalCallback.current);
            }
        }

        return () => {
            clearInterval(scrollIntervalCallback.current);
        };
    }, [show, scroll]);

    // effect to remove text formatting when pasting in rich text
    React.useEffect(() => {
        const pasteCallback = (e: ClipboardEvent) => {
            e.preventDefault();
            if (e.clipboardData === null) return;

            // get where the cursor is so we can manually override the paste
            const selection = window.getSelection();
            if (!selection || !selection.rangeCount) return;

            // if there is a text selection, delete them on paste
            selection.getRangeAt(0).deleteContents();

            // unformat the rich text by dumping the text data from clipboard into a text node and insert that instead
            const paste = e.clipboardData.getData('text');
            selection.getRangeAt(0).insertNode(document.createTextNode(paste));
        };

        const prompterElement = prompterRef.current;
        if (prompterElement) {
            prompterElement.addEventListener('paste', pasteCallback);
        }

        // cleanup
        return () => {
            if (prompterElement) {
                prompterElement.removeEventListener('paste', pasteCallback);
            }
        };
    }, []);

    return (
        <div
            className={classNames(styles['prompter-container'], {
                'd-none': !show,
                [styles['is-overlay']]: isOverlay,
            })}
        >
            <div className={styles['prompter-contents']}>
                <div className="h-100 py-l5-48 px-l5-16">
                    <h4
                        ref={prompterRef}
                        className={`${styles['prompter-script']} position-relative h-100 pt-l5-24 px-l5-4 text-white overflow-y-scroll overflow-x-hidden`}
                        contentEditable="true"
                        suppressContentEditableWarning
                        onScroll={handleScroll}
                        onWheel={handleWheelScroll}
                        onFocus={handlePrompterFocus}
                        onBlur={handlePrompterBlur}
                    >
                        {defaultText}
                    </h4>
                </div>
                <div className={`${styles['prompter-close']} d-flex justify-content-center`}>
                    <Button
                        iconLeft="close"
                        color={BUTTON_COLORS.SIMPLE}
                        width={BUTTON_WIDTHS.NO_MIN_WIDTH}
                        size={BUTTON_SIZES.LG}
                        onClick={handleOnHide}
                    />
                </div>
                <div className={styles['prompter-control-container']}>
                    <Select<number>
                        isMulti={false}
                        value={selectedSpeedOption}
                        options={PROMPTER_SELECT_SPEED_OPTIONS}
                        onChange={handleSelectSpeed}
                        dropUp
                        components={{
                            SingleValue: SelectSpeedValueComponent,
                        }}
                    />
                </div>
            </div>
        </div>
    );
});

export default Prompter;
